import { useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/common/Button';
import { useHaptics } from '@/hooks/useHaptics';
import {
  claimIsRepeatable,
  claimOutcome,
  claimOutcomeFromError,
  claimOutcomeKey,
  type ClaimOutcome,
} from '@/lib/missions/claim';
import {
  completionStamp,
  displayProgress,
  effectiveState,
  isClaimable,
  progressRatio,
  rewardCatalogRef,
  rewardEssences,
  showsNotYetAvailable,
  showsReward,
  timeLeftMs,
  timeLeftParts,
  type EffectiveState,
} from '@/lib/missions/model';
import { SlotRigaMissione } from '@/lib/missions/slotRigaMissione';
import { useClaimMissionMutation } from '@/services/questApi';
import type { MissionView } from '@/types/quest';

/** Colore dello stato. Solo token gia' in uso nel repo — nessun vocabolario visivo nuovo. */
const STATE_TONE: Record<EffectiveState, string> = {
  completed: 'text-brand',
  active: 'text-fg-muted',
  claimed: 'text-fg-muted',
  expired: 'text-fg-muted',
  unknown: 'text-fg-muted',
};

/**
 * Il colore dell'esito, per COMPORTAMENTO e non per codice HTTP.
 *
 * 🔴 `retryable` e `reauth` NON sono `text-danger`, ed e' una scelta di merito, non di gusto: su
 * `503` il premio resta dovuto e l'istanza resta riscuotibile. Dipingere di rosso un premio che
 * l'utente avra' comunque e' la stessa bugia della copy che dice «errore» — solo detta col colore,
 * dove nessun verificatore di stringhe la prenderebbe.
 */
const OUTCOME_TONE: Record<ClaimOutcome['kind'], string> = {
  claimed: 'text-brand',
  stale: 'text-fg-muted',
  retryable: 'text-warning',
  reauth: 'text-warning',
  // Neutro come `stale`: non e' un errore dell'utente ne' un premio in bilico — e' una
  // configurazione dell'ambiente, e il rosso manderebbe a cercare un guasto che non c'e'.
  unsupported: 'text-fg-muted',
};

interface MissionRowProps {
  mission: MissionView;
  /** L'istante entra come prop: la riga non legge l'orologio, cosi' e' resa deterministica.
   *  E — la ragione vera — tutte le righe di una stessa pagina concordano su cosa e' scaduto:
   *  con `new Date()` dentro ogni riga, due missioni con la stessa scadenza possono comparire
   *  una scaduta e una no, e nei GRUPPI questo significa la stessa riga in due sezioni diverse
   *  fra un render e l'altro. */
  now: Date;
}

/**
 * Una riga della lista: cosa chiede la missione, a che punto sei, cosa da', quanto manca — e, se
 * e' riscuotibile, il bottone che la incassa.
 *
 * 🔴 IL BOTTONE E' IL PRIMO GESTO DELLA PIATTAFORMA CHE MUOVE DENARO SU RICHIESTA DELL'UTENTE, e
 * il codice qui sotto e' scritto attorno a tre cose che non deve mai fare:
 *
 *  1. **Partire due volte per una pressione sola.** `disabled` mentre e' in volo, piu' una guardia
 *     su ref: `disabled` copre il caso normale, il ref copre la finestra fra il click e il render
 *     che lo disabilita. Non ci si affida all'idempotenza del server — quella e' la rete di
 *     sicurezza di un ALTRO servizio, non un permesso di essere sciatti qui.
 *  2. **Dire «pagato» quando non lo e'.** Nessuna stringa di questa riga parla di accredito: il
 *     vocabolario ammesso e' «riscossa» e «premio in arrivo». Il perche' sta in `@/lib/missions/claim`.
 *  3. **Mostrare come riscossa una riga per cui il server non ha risposto `ok`.** La riga non
 *     scrive nulla nella cache: mostra l'esito che ha ricevuto, e la verita' arriva dal refetch.
 *
 * ⚠️ E la cosa che DEVE fare: non lasciare mai un esito muto. Otto esiti, otto facce — mappate in
 * `claimOutcome`, non qui, cosi' che siano verificabili senza montare React.
 */
export function MissionRow({ mission, now }: MissionRowProps) {
  const { t, i18n } = useTranslation('missions');
  const haptics = useHaptics();
  const [claim, { isLoading, data, error, isSuccess, isError }] = useClaimMissionMutation();

  // Il cancello che il `disabled` non copre: fra la pressione e il render che disabilita il
  // bottone c'e' una finestra. E' stretta (React sincronizza gli eventi discreti) e su una rotta
  // qualunque non varrebbe una riga di codice — su questa si'.
  const inFlight = useRef(false);
  const Riga = useContext(SlotRigaMissione);

  const state = effectiveState(mission, now);
  const remaining = timeLeftMs(mission, now);
  const ratio = progressRatio(mission);
  const withReward = showsReward(mission, now);
  const essences = rewardEssences(mission);
  const catalogRef = rewardCatalogRef(mission);
  const stamp = completionStamp(mission);
  // Se la missione aspetta un evento che nessuno ha mai emesso. La regola sta in `model`, con i
  // suoi due cancelli: qui resta solo la resa.
  const notYet = showsNotYetAvailable(mission, now);

  // L'esito della NOSTRA chiamata, se ce n'e' stata una. Vive nello stato della mutation — cioe'
  // per riga, perche' ogni riga monta il proprio hook — e non nella cache della lista: e' cio' che
  // rende impossibile "mostrare riscossa" una riga senza un `ok` del server.
  // Lo stato HTTP e' 200 per costruzione: solo un 2xx arriva a `isSuccess`, e cio' su cui la mappa
  // decide davvero e' il `ok:true` nel corpo.
  const outcome: ClaimOutcome | null = isSuccess
    ? claimOutcome(200, data)
    : isError
      ? claimOutcomeFromError(error)
      : null;

  // Il bottone c'e' finche' il gesto ha senso: quando la riga e' riscuotibile e non abbiamo ancora
  // un esito che lo chiuda. `claimIsRepeatable` lo tiene su `503`, rete caduta e riautenticazione —
  // i casi in cui il premio e' ancora dovuto. Toglierlo li' rimetterebbe l'utente esattamente dove
  // stava prima di questo lavoro: davanti a un'istruzione senza il gesto corrispondente.
  const showClaim = isClaimable(mission, now) && (outcome === null || claimIsRepeatable(outcome));

  const onClaim = (): void => {
    if (inFlight.current || isLoading) return;
    inFlight.current = true;
    haptics.impact('medium');
    void claim(mission.instanceId)
      .unwrap()
      .then(
        (body) => claimOutcome(200, body),
        (err: unknown) => claimOutcomeFromError(err),
      )
      .then((settled) => {
        inFlight.current = false;
        // Riscossa = successo anche con il trasferimento ancora `pending` (il claim E' passato) e
        // anche su `already_claimed` (era gia' passato). Tutto il resto vibra come AVVISO e mai
        // come errore secco: su 503 e su una rete caduta non e' andato perso nulla.
        haptics.notification(settled.kind === 'claimed' ? 'success' : 'warning');
      });
  };

  // Il titolo lo manda QUEST (`MissionView.title`, sempre non vuoto — decisione Luca 2026-08-27).
  // ⚠️ Qui c'era una tabella i18n di 16 titoli derivati dal seed: e' stata CANCELLATA, non
  // adattata. Era, alla lettera, "una copia del catalogo sull'altro lato del filo" — e i template
  // sono editabili dall'admin SENZA deploy, quindi divergeva dal primo template nuovo e nessuno se
  // ne sarebbe accorto. Il campo `title` esiste per eliminarla: ricostruirla qui, anche solo come
  // fallback, ricrearebbe il problema gia' chiuso.
  const title = mission.title;

  const expiryLabel =
    remaining === null
      ? t('expires.never')
      : remaining === 0
        ? t('expires.gone')
        : t('expires.in', { time: formatParts(remaining, t) });

  if (Riga) return <Riga vista={{ mission, title, state, remaining, ratio, withReward, essences, catalogRef, stamp, notYet, expiryLabel, outcome, showClaim, isLoading, onClaim }} />;

  return (
    <li className="rounded-xl bg-bg-secondary p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {/* 🔴 LA PARENTESI NON STA DENTRO IL `truncate`, ED E' IL PUNTO DELICATO DELLA RESA.
              Appesa in coda al titolo dentro lo stesso blocco troncato, sparirebbe proprio sui
              titoli lunghi — cioe' in silenzio, e senza che nulla lo segnali. Qui il titolo
              continua a troncare (`truncate` su un flex item con `overflow:hidden` puo' scendere
              sotto la larghezza del contenuto) e l'etichetta e' `shrink-0`: se lo spazio non
              basta, a cedere e' il titolo, mai l'informazione che la missione non e' giocabile.

              ⚠️ La parola e' scelta, non di comodo. «Inattiva» in italiano suona come uno stato
              DECISO da qualcuno — spenta, rotta, sospesa; la verita' e' TEMPORALE: quell'evento
              non e' ancora mai arrivato. Le parentesi stanno nella stringa tradotta perche' la
              punteggiatura appartiene alla lingua, non al layout. */}
          <p className="flex items-baseline gap-1 text-sm font-bold text-fg">
            <span className="truncate">{title}</span>
            {notYet && (
              <span className="shrink-0 font-medium text-fg-muted">{t('availability.notYet')}</span>
            )}
          </p>
          <p className={`mt-0.5 text-xs font-medium ${STATE_TONE[state]}`}>
            {t(`state.${state}`)}
            {/* La finestra come ETICHETTA sulla riga, non come raggruppamento: i gruppi sono quelli
                di stato, e incrociarli con le tre finestre darebbe quindici sezioni per una
                manciata di righe. Letta da `window`, MAI dedotta da `expiresAt`. */}
            <span className="text-fg-muted"> · {t(`window.${mission.window}`)}</span>
          </p>
        </div>
        {withReward && (
          <div className="shrink-0 text-right">
            <p className="text-sm font-bold text-brand">
              {essences !== null ? t('reward.essences', { count: essences }) : t('reward.cosmetic')}
            </p>
            {catalogRef !== null && (
              <p className="mt-0.5 truncate text-xs text-fg-muted">{catalogRef}</p>
            )}
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center gap-3">
        {/* La barra e' decorativa: il valore vero e' nel testo accanto, che i lettori di schermo
            leggono. `aria-hidden` evita di annunciare due volte la stessa cosa. */}
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-bg-tertiary" aria-hidden="true">
          <div
            className="h-full rounded-full bg-brand transition-[width] duration-300"
            style={{ width: `${ratio * 100}%` }}
          />
        </div>
        <span className="shrink-0 text-xs tabular-nums text-fg-muted">
          {t('progress', { current: displayProgress(mission), target: mission.target })}
        </span>
      </div>

      {/* La data compare quando Quest proietta `completedAt`/`claimedAt`. Fino ad allora
          `completionStamp` torna null e la riga mostra solo il premio — vero, e non promette nulla.
          NON si ripiega su `expiresAt` (timbro dei 7 giorni di riscossione: mostrerebbe una data nel
          FUTURO).

          🔴 DUE CHIAVI, NON UNA. La stringa segue il campo che la regge: `stamp.kind` decide, e non
          e' un dettaglio di stile. «Riscossa il» dice che il giocatore ha CHIESTO il premio, che e'
          tutto cio' che `claimedAt` sa: la data viene stampigliata anche se il trasferimento QBank
          e' tornato retryable/failed, e lo stato di consegna non e' esposto ad alcuna rotta
          giocatore (ADR-0073). «Ricevuta il» sarebbe una bugia che nulla potra' mai smentire. */}
      <p className="mt-2 text-xs text-fg-muted">
        {stamp !== null
          ? t(`stamp.${stamp.kind}`, {
              date: new Date(stamp.ms).toLocaleDateString(i18n.language),
            })
          : expiryLabel}
      </p>

      {showClaim && (
        <Button
          fullWidth
          className="mt-3"
          onClick={onClaim}
          disabled={isLoading}
          aria-busy={isLoading}
        >
          {isLoading ? t('claim.sending') : t('claim.action')}
        </Button>
      )}

      {/* L'esito, sempre. `role="status"` lo annuncia ai lettori di schermo senza rubare il fuoco:
          e' un fatto che arriva, non un allarme da interrompere. */}
      {outcome !== null && (
        <p role="status" className={`mt-2 text-xs font-medium ${OUTCOME_TONE[outcome.kind]}`}>
          {t(claimOutcomeKey(outcome))}
        </p>
      )}
    </li>
  );
}

/** Traduce l'unita' scelta da `timeLeftParts` — la regola e' pura, qui resta solo la resa. */
function formatParts(ms: number, t: ReturnType<typeof useTranslation>['t']): string {
  const parts = timeLeftParts(ms);
  return parts.unit === 'soon' ? t('time.soon') : t(`time.${parts.unit}`, { count: parts.count });
}
